import React from 'react'

import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'

import { authActions } from '@services'

import { formatDate } from '@helpers'

import { Button } from '@components/ui/Buttons'
import { Grid } from '@components/ui/Grid'
import { Typography } from '@components/ui/Typography'
import { Paper } from '@components/ui/Wrappers'

import { BADGE_MASTER } from '@constants'

import { getCompanyUserBadge } from './helpers'
import { UserBadge } from './UserBadge'

const UserBadgeContainer = styled.div`
  margin-bottom: 15px;
  display: inline-block;
  position: relative;
  margin-top: -107px;
`

const RootGrid = styled(Grid)`
  flex: 1;
`

const StyledPaper = styled(Paper)`
  padding-top: 70px;
  padding-left: 45px;
  padding-right: 45px;
  padding-bottom: 85px;
  max-width: 540px;
  text-align: center;
  position: relative;
`

const StyledButton = styled(Button)`
  position: absolute;
  top: 20px;
  right: 20px;
`

const BadgeType = styled.span`
  display: block;
  border: 1px solid ${({ theme }) => theme.colors.common.inputBorder};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.common.inputBackground};
  margin: 35px auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.secondary[60]};
  padding: 15px;
  font-size: 22px;
  font-weight: 700;
  min-width: 180px;
  max-width: 260px;
`

interface UserBadgeViewProps {
  badgeInfo: {
    badge: Nullable<1 | 2>
  }
  callUpdateBadgeDisplayedAt: (payload: string) => void
  onClose: VoidFunction
}

function PureUserBadgeView({ badgeInfo: { badge }, callUpdateBadgeDisplayedAt, onClose }: UserBadgeViewProps) {
  React.useEffect(() => {
    // call auth/me patch with "badge_displayed_at"
    const payload = formatDate(new Date().toISOString())
    callUpdateBadgeDisplayedAt(payload)
  }, [callUpdateBadgeDisplayedAt])

  return (
    <RootGrid container alignItems="center" justifyContent="center" direction="column">
      <StyledPaper>
        <UserBadgeContainer>
          <UserBadge badge={badge} size="big" />
        </UserBadgeContainer>
        <Typography tag="h3" size="heading-3" align="center" color="gray-80" style={{ marginBottom: 30 }}>
          <FormattedMessage id="badgeView.accost" defaultMessage="Kedves Felhasználónk!" />
        </Typography>

        <Typography size="400-md" align="center" color="gray-80">
          {badge === BADGE_MASTER ? (
            <FormattedMessage
              id="badgeView.content.master"
              defaultMessage="Rengetegszer segítettél nekünk jobbá tenni a Keret- és adófigyelőt és köszönjük, hogy ajánlottad programunkat. Hálánk jeléül fiókod kiemelt {rank} rangot kapott, és örökre díjmentesen használhatod a Keret- és adófigyelőt."
              values={{
                rank: (
                  <BadgeType>
                    <FormattedMessage id="badgeView.quickBadgeTitle.master" defaultMessage=",,Kvikkmester”" />
                  </BadgeType>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="badgeView.content.assistant"
              defaultMessage="Rengetegszer segítettél nekünk jobbá tenni a Keret- és adófigyelőt és köszönjük, hogy ajánlottad programunkat. Hálánk jeléül fiókod kiemelt {rank} rangot kapott, és a következő 6 hónapban díjmentesen használhatod a Keret- és adófigyelőt."
              values={{
                rank: (
                  <BadgeType>
                    <FormattedMessage id="badgeView.quickBadgeTitle.assistant" defaultMessage=",,Kvikk segéd”" />
                  </BadgeType>
                ),
              }}
            />
          )}
        </Typography>

        <StyledButton size="small" variant="secondaryText" onClick={onClose}>
          <FormattedMessage id="badgeView.button.goToDashboard" defaultMessage="Tovább a kezdőlapra" />
        </StyledButton>
      </StyledPaper>
    </RootGrid>
  )
}

export const UserBadgeView = connect(
  (state: Store) => ({
    badgeInfo: getCompanyUserBadge(state),
  }),
  {
    callUpdateBadgeDisplayedAt: authActions.updateBadgeDisplayedAt.request,
  }
)(PureUserBadgeView)

UserBadgeView.displayName = 'UserBadgeView'
